/**
 * usePortfolioScan - Hook for managing portfolio-wide lightweight analysis.
 *
 * Handles:
 * - SSE streaming from /api/v1/portfolio/scan/stream
 * - Per-ticker scan result tracking
 * - Progress counts (cached, analyzing, complete, failed)
 * - Cached results loading for returning users
 */

import { useState, useCallback, useRef } from 'react';
import { API_BASE_URL } from '@/lib/api';
import type { ScanResult, ScanProgress } from '@/types/portfolio';

type ScanOverallStatus = 'idle' | 'scanning' | 'complete' | 'error';

interface UsePortfolioScanReturn {
  scanResults: Map<string, ScanResult>;
  scanStatus: ScanOverallStatus;
  progress: ScanProgress;
  estimatedCredits: number;
  error: string | null;
  insufficientCredits: { required: number; available: number } | null;
  startScan: (forceRefresh?: boolean) => void;
  cancelScan: () => void;
  loadCachedResults: () => Promise<void>;
}

export function usePortfolioScan(): UsePortfolioScanReturn {
  const [scanResults, setScanResults] = useState<Map<string, ScanResult>>(new Map());
  const [scanStatus, setScanStatus] = useState<ScanOverallStatus>('idle');
  const [progress, setProgress] = useState<ScanProgress>({
    total: 0, completed: 0, cached: 0, failed: 0,
  });
  const [estimatedCredits, setEstimatedCredits] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [insufficientCredits, setInsufficientCredits] = useState<{
    required: number; available: number;
  } | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const cancelScan = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setScanStatus('idle');
  }, []);

  const startScan = useCallback((forceRefresh = false) => {
    // Cancel any existing scan
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    // Reset state
    setScanResults(new Map());
    setScanStatus('scanning');
    setProgress({ total: 0, completed: 0, cached: 0, failed: 0 });
    setError(null);
    setInsufficientCredits(null);

    const token = localStorage.getItem('access_token');

    (async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/v1/portfolio/scan/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            body: JSON.stringify({ force_refresh: forceRefresh }),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || `HTTP ${response.status}`);
        }

        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let eventType = '';
          let eventData = '';

          for (const line of lines) {
            if (line.startsWith('event: ')) {
              eventType = line.slice(7).trim();
            } else if (line.startsWith('data: ')) {
              eventData = line.slice(6);
            } else if (line === '' && eventType && eventData) {
              handleEvent(eventType, eventData);
              eventType = '';
              eventData = '';
            }
          }
        }

        // Stream ended — mark complete if not already
        setScanStatus((prev) => (prev === 'scanning' ? 'complete' : prev));
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        const msg = e instanceof Error ? e.message : 'Scan failed';
        setError(msg);
        setScanStatus('error');
      }
    })();

    function handleEvent(type: string, rawData: string) {
      try {
        const data = JSON.parse(rawData);

        switch (type) {
          case 'scan_start':
            setProgress({
              total: data.total || 0,
              completed: 0,
              cached: data.cached_count || 0,
              failed: 0,
            });
            setEstimatedCredits(data.estimated_credits || 0);
            break;

          case 'ticker_cached':
            if (data.result) {
              const result: ScanResult = { ...data.result, cached: true };
              setScanResults((prev) => {
                const next = new Map(prev);
                next.set(data.ticker, result);
                return next;
              });
              setProgress((prev) => ({
                ...prev,
                completed: prev.completed + 1,
              }));
            }
            break;

          case 'ticker_analyzing':
            // No state change needed — card already shows skeleton
            break;

          case 'ticker_complete':
            if (data.result) {
              const result: ScanResult = { ...data.result, cached: false };
              setScanResults((prev) => {
                const next = new Map(prev);
                next.set(data.ticker, result);
                return next;
              });
              setProgress((prev) => ({
                ...prev,
                completed: prev.completed + 1,
              }));
            }
            break;

          case 'ticker_failed':
            setProgress((prev) => ({
              ...prev,
              failed: prev.failed + 1,
            }));
            break;

          case 'scan_complete':
            setScanStatus('complete');
            break;

          case 'scan_insufficient_credits':
            setInsufficientCredits({
              required: data.required,
              available: data.available,
            });
            setScanStatus('error');
            break;

          case 'scan_error':
            setError(data.error || 'Scan failed');
            setScanStatus('error');
            break;

          default:
            break;
        }
      } catch {
        // Ignore parse errors on individual events
      }
    }
  }, []);

  const loadCachedResults = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/portfolio/scan/results`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
        }
      );

      if (!response.ok) return;

      const data = await response.json();
      if (data.results && typeof data.results === 'object') {
        const map = new Map<string, ScanResult>();
        for (const [ticker, result] of Object.entries(data.results)) {
          map.set(ticker, { ...(result as ScanResult), cached: true });
        }
        setScanResults(map);
        setProgress({
          total: data.total || 0,
          completed: map.size,
          cached: map.size,
          failed: 0,
        });
        if (map.size > 0) {
          setScanStatus('complete');
        }
      }
    } catch {
      // Silently fail — cached results are optional
    }
  }, []);

  return {
    scanResults,
    scanStatus,
    progress,
    estimatedCredits,
    error,
    insufficientCredits,
    startScan,
    cancelScan,
    loadCachedResults,
  };
}
